<template>
 <div>
  <div class="ts-flex ts-justify-between">
   <div class="ts-mb-4">
    <h1 class="page-header ts-mb-1">{{ $t('invoiceAdjustment.adjustmentDetail') }}</h1>
    <div class="ts-flex ts-items-center ts-space-x-2">
     <p class="ts-text-xs ts-text-gray-600">
      {{ $t('invoiceAdjustment.invoice') }} {{ summaryInvoice ? summaryInvoice.inv_number : '' }}
     </p>
     <span class="ts-bg-blue-100 ts-text-blue-500 ts-px-3 ts-py-1 ts-rounded-full ts-font-bold ts-uppercase ts-tracking-wider ts-border ts-border-blue-200" style="font-size: 8px;">
      {{ $t('invoiceAdjustment.completed') }}
     </span>
    </div>
   </div>
   <ts-breadcrumb :items="[
    { text: $t('toHomePage'), href: '/' },
    { text: $t('invoiceAdjustment.pageTitle'), href: '/payment-adjustments' },
    { text: $t('view'), active: true }
   ]" />
  </div>

  <ts-loading-banner :loading="loading">
   <div class="ts-bg-white ts-rounded-xl ts-border ts-border-border-light ts-overflow-hidden ts-mb-6">
    <div style="background-color: #F8FAFC;" class="ts-px-6 ts-py-4 ts-border-b ts-border-border-light">
     <h2 class="text-navy-dark ts-text-lg ts-font-bold ts-flex ts-items-center ts-gap-2">
      <span class="material-icons text-primary">history</span>
      {{ $t('invoiceAdjustment.originalInvoiceSummary') }}
     </h2>
    </div>

    <div class="ts-grid ts-grid-cols-1 md:ts-grid-cols-5">
     <div class="ts-p-4 ts-flex ts-flex-col ts-gap-1">
      <p style="font-size: 10px;" class="ts-text-gray-500 ts-font-bold ts-uppercase ts-tracking-wider">
       {{ $t('invoiceAdjustment.program') }}
      </p>
      <p class="ts-text-gray-800 ts-text-sm ts-font-medium">{{ summaryInvoice ? summaryInvoice.acp_name_foreign : '' }}</p>
     </div>

     <div class="ts-p-4 ts-flex ts-flex-col ts-gap-1">
      <p style="font-size: 10px;" class="ts-text-gray-500 ts-font-bold ts-uppercase ts-tracking-wider">
       {{ $t('invoiceAdjustment.level') }}
      </p>
      <p class="ts-text-gray-800 ts-text-sm ts-font-medium">{{ summaryInvoice ? summaryInvoice.level_name_foreign : '' }}</p>
     </div>

     <div class="ts-p-4 ts-flex ts-flex-col ts-gap-1">
      <p style="font-size: 10px;" class="ts-text-gray-500 ts-font-bold ts-uppercase ts-tracking-wider">
       {{ $t('invoiceAdjustment.grade') }}
      </p>
      <p class="ts-text-gray-800 ts-text-sm ts-font-medium">{{ summaryInvoice ? summaryInvoice.grade_name_foreign : '' }}</p>
     </div>

     <div class="ts-p-4 ts-flex ts-flex-col ts-gap-1">
      <p style="font-size: 10px;" class="ts-text-gray-500 ts-font-bold ts-uppercase ts-tracking-wider">
       {{ $t('invoiceAdjustment.class') }}
      </p>
      <p class="ts-text-gray-800 ts-text-sm ts-font-medium">{{ summaryInvoice ? summaryInvoice.old_class : '' }} </p>
     </div>

     <div class="ts-p-4 ts-flex ts-flex-col ts-gap-1 bg-primary/[0.03]">
      <p style="font-size: 10px;" class="ts-text-gray-500 ts-font-bold ts-uppercase ts-tracking-wider">
       {{ $t('invoiceAdjustment.originalTotal') }}
      </p>
      <p class="text-navy-dark ts-text-sm ts-font-bold">
       {{ defaultCurrency ? defaultCurrency.currency_symbol : '' }}
       {{ summaryInvoice ? summaryInvoice.total_amount : '' }}
      </p>
     </div>
    </div>
   </div>

   <!-- ======================== -->

   <div class="ts-grid ts-grid-cols-1 lg:ts-grid-cols-2 ts-gap-6 ts-mb-6">
    <div class="ts-bg-white ts-rounded-xl ts-border ts-border-border-light ts-overflow-hidden">
     <div class="bg-slate-50 ts-px-6 ts-py-4 ts-border-b ts-border-border-light">
      <h2 class="text-navy-dark ts-text-lg ts-font-bold ts-flex ts-items-center ts-gap-2">
       <span class="material-icons text-primary">edit_document</span>
       {{ $t('invoiceAdjustment.adjustmentApplied') }}
      </h2>
     </div>

     <div class="ts-p-6 ts-space-y-4">
      <div class="ts-grid ts-grid-cols-2 ts-gap-4">
       <div>
        <p class="text-text-secondary text-10 ts-font-bold ts-uppercase ts-tracking-wider">
         {{ $t('invoiceAdjustment.newClassAssignment') }}
        </p>
        <p class="text-text-main text-base font-medium">
         {{ summaryInvoice ? summaryInvoice.new_class : '' }}
        </p>
       </div>

       <div>
        <p class="text-text-secondary text-10 ts-font-bold ts-uppercase ts-tracking-wider">
         {{ $t('invoiceAdjustment.adjustmentAmount') }}
        </p>
        <p class="text-red-600 text-base font-bold">
         {{ defaultCurrency ? defaultCurrency.currency_symbol : '' }}
         {{ summaryInvoice ? formatNumber(summaryInvoice.adj_amount) : '' }}
        </p>
       </div>
      </div>

      <div class="pt-2 border-t border-border-light">
       <p class="text-text-secondary text-10 ts-font-bold ts-uppercase ts-tracking-wider">
        {{ $t('invoiceAdjustment.remarks') }}
       </p>
       <p class="text-text-main ts-text-sm ts-leading-relaxed ts-mt-1 ts-italic ts-opacity-50">
        {{ summaryInvoice ? summaryInvoice.descriptions : '' }}
       </p>
      </div>
     </div>
    </div>

    <div class="ts-bg-white ts-rounded-xl ts-border ts-border-border-light ts-overflow-hidden">
     <div class="bg-slate-50 ts-px-6 ts-py-4 ts-border-b ts-border-border-light">
      <h2 class="text-navy-dark ts-text-lg ts-font-bold ts-flex ts-items-center ts-gap-2">
       <span class="material-icons text-primary">payments</span>
       {{ $t('invoiceAdjustment.paymentRefundDetails') }}
      </h2>
     </div>

     <div class="ts-p-6 ts-space-y-2">
      <div class="ts-grid ts-grid-cols-2 ts-gap-y-4 ts-gap-x-6" v-for="value in paymentReceived">
       <div>
        <p class="text-text-secondary text-10 ts-font-bold ts-uppercase ts-tracking-wider">
         {{ $t('invoiceAdjustment.paymentMethod') }}
        </p>
        <p class="text-text-main ts-text-sm ts-font-medium">
         {{ value.payment_method }} {{ value.payment_method_id !== 1 ? `(${value.account_name + ' - ' + value.account_number})` : '' }}
        </p>
       </div>

       <div>
        <p class="text-text-secondary text-10 ts-font-bold ts-uppercase ts-tracking-wider">
         {{ $t('invoiceAdjustment.amountFxRate') }}
        </p>
        <p class="text-text-main ts-text-sm ts-font-medium">
         {{ value.currency_symbol }} {{ formatNumber(value.amount_original) }} : {{ formatNumber(value.fx_rate) }}
        </p>
       </div>
      </div>
     </div>
    </div>
   </div>

   <div class="ts-bg-white ts-rounded-xl ts-border ts-border-border-light ts-overflow-hidden ts-mb-6">
    <div class="bg-slate-50 ts-px-6 ts-py-4 ts-border-b ts-border-border-light">
     <h2 class="text-navy-dark ts-text-lg ts-font-bold ts-flex ts-items-center ts-gap-2">
      <span class="material-icons text-primary">list_alt</span>
      {{ $t('invoiceAdjustment.finalModifiedItems') }}
     </h2>
    </div>

    <div class="ts-overflow-x-auto">
     <table class="ts-w-full ts-text-left ts-border-collapse">
      <thead>
       <tr class="bg-slate-50 ts-border-b ts-border-border-light">
        <th class="ts-px-6 ts-py-3 text-text-secondary text-10 ts-font-bold ts-uppercase ts-tracking-wider">
         {{ $t('invoiceAdjustment.feeType') }}
        </th>
        <th class="ts-px-6 ts-py-3 text-text-secondary text-10 ts-font-bold ts-uppercase ts-tracking-wider">
         {{ $t('invoiceAdjustment.oldFee') }}
        </th>
        <th class="ts-px-6 ts-py-3 text-text-secondary text-10 ts-font-bold ts-uppercase ts-tracking-wider">
         {{ $t('invoiceAdjustment.currentFee') }}
        </th>
        <th class="ts-px-6 ts-py-3 text-text-secondary text-10 ts-font-bold ts-uppercase ts-tracking-wider ts-text-center">
         {{ $t('invoiceAdjustment.qty') }}
        </th>
        <th class="ts-px-6 ts-py-3 text-text-secondary text-10 ts-font-bold ts-uppercase ts-tracking-wider ts-text-right">
         {{ $t('invoiceAdjustment.unitFee') }}
        </th>
        <th class="ts-px-6 ts-py-3 text-text-secondary text-10 ts-font-bold ts-uppercase ts-tracking-wider">
         {{ $t('invoiceAdjustment.period') }}
        </th>
        <th class="ts-px-6 ts-py-3 text-text-secondary text-10 ts-font-bold ts-uppercase ts-tracking-wider ts-text-right">
         {{ $t('invoiceAdjustment.total') }}
        </th>
       </tr>
      </thead>

      <tbody class="ts-divide-y">
       <tr class="hover:ts-bg-gray-300" v-for="value in details">
        <td class="ts-px-6 ts-py-2 ts-text-sm ts-font-semibold">{{ value.fee_type_name }}</td>
        <td class="ts-px-6 ts-py-2 ts-text-sm text-text-secondary">{{ value.old_fee_name_en }} </td>
        <td class="ts-px-6 ts-py-2 ts-text-sm text-text-secondary">{{ value.fee_name_en }} </td>
        <td class="ts-px-6 ts-py-2 ts-text-sm text-center text-text-main">{{ value.quantity }}</td>
        <td class="ts-px-6 ts-py-2 ts-text-sm ts-text-right text-text-main">
         {{ defaultCurrency ? defaultCurrency.currency_symbol : '' }} {{ formatNumber(value.fee_amount) }}
        </td>
        <td class="ts-px-6 ts-py-2 ts-text-sm text-text-secondary">{{ value.fee_start_date }} - {{ value.fee_end_date }}</td>
        <td class="ts-px-6 ts-py-2 ts-text-sm ts-text-right ts-font-bold text-navy-dark">
         {{ defaultCurrency ? defaultCurrency.currency_symbol : '' }} {{ calculateAmount(value) }}
        </td>
       </tr>
      </tbody>
     </table>
    </div>
   </div>

   <footer class=" ts-rounded-md ts-bg-white ts-flex ts-items-center ts-justify-end ts-gap-4 ts-p-4 ts-border-t ts-border-slate-200 dark:ts-border-slate-800">
    <ts-button @click="onCancel">
     {{ $t('cancel') }}
    </ts-button>
   </footer>
  </ts-loading-banner>
 </div>
</template>


<script>
import { isNumber, values } from 'lodash';
import { mapActions, mapState } from 'vuex';
export default {
	name: "AdjustmentShow",
	data() {
		return {
			loading: false,
			paymentReceived: [],
			summaryInvoice: {},
			details: []
		}
	},
	mounted() {
		this.fetch()
	},
	computed: {
		...mapState('invoiceAjustment', ['currencies']),
		defaultCurrency() {
			return this.currencies.find(c => c.is_default)
		},
	},
	methods: {
		...mapActions('invoiceAjustment', [
			'show',
			'onGetCurrency'
		]),
		onCancel() {
			this.$router.go(-1);
		},
		async fetch() {
			this.loading = true
			this.paymentReceived = []
			this.summaryInvoice = {}
			this.details = []
			const id = this.$route.params.id
			await this.onGetCurrency()
			await this.show(id)
				.then(({ data }) => {
					this.paymentReceived = data.payment_receive
					this.summaryInvoice = data.summary
					this.details = data.detail
				})
				.finally(() => this.loading = false)
		},
		formatNumber(value) {
			return new Intl.NumberFormat().format(
				parseFloat(value),
			)
		},
		calculateAmount(item) {
			return (item.quantity * item.fee_amount).toFixed(2)
		},
	}
}

</script>

<style scoped>
.text-havy-dark {
	color: "#0f172a"
}

.bg-slate-50 {
	background-color: #F8FAFC;
}

.text-text-secondary {
	color: #64748b;
}

.text-10 {
	font-size: 10px;
}

.text-text-main {
	color: #1e293b;
}
</style>